import React, { Fragment, useMemo } from 'react';
import { IBasicFilterFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import BasicFilterCondition from './BasicFilterCondition';
import { useDndEntityInternalContext } from '../../../DndEntity';
import { IRowMeta } from '../../../../redux/slices/oldBuilder';
import { getDndActiveItemData } from '../../../DndEntity/utils';
import { MAX_FILTER_CONDITIONS } from '../../../../config/constants';
import * as BasicFilterViewUI from './BasicFilterViewUI';
import BasicFilterViewDroppable from './BasicFilterViewDroppable';

/**
 * This component should represent filter Conditions as a SINGLE sentence.
 *
 * Each Condition has an open bracket in the beginning and a closing one
 * in the end.
 *
 * Each Condition can render multiple predicates with logic operator
 * between them.
 *
 * Each condition Predicate can render multiple Operands with logic operator
 * between them.
 *
 * Everything is styled separately and that is intentional to eventually make
 * a single sentence.
 *
 * Keep in mind that operand names can be long!
 */

interface IProps {
  rowMeta?: IRowMeta;
  filter: IBasicFilterFieldsFragment;
}

const BasicFilterView = ({ rowMeta, filter }: IProps) => {
  const { conditions, logicOps } = filter;
  const dndContext = useDndEntityInternalContext();

  const showAddFilterDropzone = useMemo(() => {
    if (dndContext) {
      const data = getDndActiveItemData(dndContext.activeItem);

      if (data) {
        return (
          data.__typename === 'TagLeafField' &&
          conditions.length < MAX_FILTER_CONDITIONS
        );
      }
    }

    return false;
  }, [dndContext, conditions]);

  return (
    <>
      {conditions.map((condition, conditionIndex) => {
        const logicOp = logicOps?.[conditionIndex];
        const key = `basic-filter-field-view-${conditionIndex}`;

        return (
          <Fragment key={key}>
            <BasicFilterCondition
              rowMeta={rowMeta}
              filter={filter}
              condition={condition}
              conditionIndex={conditionIndex}
              keyPrefix={key}
            />
            {logicOp && (
              <BasicFilterViewUI.LogicOp>
                {logicOp.key}
              </BasicFilterViewUI.LogicOp>
            )}
            {!logicOp && showAddFilterDropzone && rowMeta && (
              <BasicFilterViewDroppable
                rowMeta={rowMeta}
                conditionIndex={conditionIndex}
                filter={filter}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default BasicFilterView;
